import React, { useEffect, useRef, useState } from 'react';
import { Select, Button, Switch, Input, Space, Typography, Popconfirm, Tooltip } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { useTranslation } from '../../i18n';
import TagsInput from '../TagsInput';

const { Text } = Typography;

/**
 * Content-Security-Policy 结构化编辑器。
 * value/onChange 仍是原来的 CSP 字符串(后端契约不变):加载时解析成「指令行」,保存时序列化回字符串。
 * 未知指令原样保留(名字和值都不改)。
 */

export const CSP_DIRECTIVES = [
  'default-src', 'script-src', 'style-src', 'img-src', 'connect-src', 'font-src', 'frame-src',
  'frame-ancestors', 'media-src', 'object-src', 'worker-src', 'manifest-src', 'child-src',
  'base-uri', 'form-action', 'script-src-elem', 'script-src-attr', 'style-src-elem', 'style-src-attr',
  'upgrade-insecure-requests', 'block-all-mixed-content', 'sandbox', 'report-uri', 'report-to',
];
// 无值指令(开关)
const FLAG_DIRECTIVES = ['upgrade-insecure-requests', 'block-all-mixed-content'];

const KEYWORDS = ['self', 'none', 'unsafe-inline', 'unsafe-eval', 'strict-dynamic', 'unsafe-hashes', 'wasm-unsafe-eval', 'report-sample'];
const QUICK = ["'self'", "'none'", "'unsafe-inline'", "'unsafe-eval'", 'data:', 'blob:', 'https:'];

const PRESETS = {
  basic: "default-src 'self'; img-src 'self' data: https:; style-src 'self' 'unsafe-inline'; font-src 'self' data: https:; object-src 'none'; base-uri 'self'; frame-ancestors 'self'",
  strict: "default-src 'none'; script-src 'self'; style-src 'self'; img-src 'self'; font-src 'self'; connect-src 'self'; object-src 'none'; base-uri 'self'; form-action 'self'; frame-ancestors 'none'; upgrade-insecure-requests",
  inline: "default-src 'self'; script-src 'self' 'unsafe-inline' 'unsafe-eval'; style-src 'self' 'unsafe-inline'; img-src * data: blob:; font-src * data:; connect-src *; frame-ancestors 'self'",
};

export const parseCsp = (str) => String(str || '')
  .split(';')
  .map((d) => d.trim())
  .filter(Boolean)
  .map((d) => {
    const [name, ...sources] = d.split(/\s+/);
    return { name: name.toLowerCase(), sources };
  });

export const serializeCsp = (rows) => rows
  .filter((r) => r.name)
  .map((r) => [r.name, ...(FLAG_DIRECTIVES.includes(r.name) ? [] : r.sources)].join(' ').trim())
  .join('; ');

// self → 'self'(用户常忘了引号)
const normSource = (s) => (KEYWORDS.includes(s.toLowerCase()) ? `'${s.toLowerCase()}'` : s);

const isValidSource = (s) => (
  /^'(self|none|unsafe-inline|unsafe-eval|strict-dynamic|unsafe-hashes|wasm-unsafe-eval|report-sample)'$/.test(s)
  || /^'(nonce-[A-Za-z0-9+/=_-]+|sha(256|384|512)-[A-Za-z0-9+/=_-]+)'$/.test(s)
  || /^[a-z][a-z0-9+.-]*:$/i.test(s)
  || s === '*'
  || /^([a-z][a-z0-9+.-]*:\/\/)?(\*\.)?[a-z0-9-]+(\.[a-z0-9-]+)*(:(\d{1,5}|\*))?(\/\S*)?$/i.test(s)
  || /^(https?:\/\/)?(\*\.)?[a-z0-9.-]+\.\*$/i.test(s)
);

const CspBuilder = ({ value, onChange, reportOnly, onReportOnlyChange, disabled }) => {
  const { t } = useTranslation();
  const [rows, setRows] = useState(() => parseCsp(value));
  const [raw, setRaw] = useState(false);
  const lastEmitted = useRef(value || '');

  // 外部(加载)值变化 → 重新解析
  useEffect(() => {
    if ((value || '') !== lastEmitted.current) {
      lastEmitted.current = value || '';
      setRows(parseCsp(value));
    }
  }, [value]);

  const emit = (next) => {
    setRows(next);
    const s = serializeCsp(next);
    lastEmitted.current = s;
    onChange?.(s);
  };
  const updateRow = (i, patch) => emit(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const used = new Set(rows.map((r) => r.name));
  const addable = CSP_DIRECTIVES.filter((d) => !used.has(d));
  const applyPreset = (k) => {
    lastEmitted.current = PRESETS[k];
    setRows(parseCsp(PRESETS[k]));
    onChange?.(PRESETS[k]);
  };

  const presetButtons = (
    <Space.Compact size="small">
      {[['basic', t('Basic')], ['strict', t('Strict')], ['inline', t('Allow inline')]].map(([k, label]) => (
        rows.length ? (
          <Popconfirm key={k} title={t('Replace the current policy?')} onConfirm={() => applyPreset(k)}>
            <Button size="small" disabled={disabled}>{label}</Button>
          </Popconfirm>
        ) : (
          <Button key={k} size="small" disabled={disabled} onClick={() => applyPreset(k)}>{label}</Button>
        )
      ))}
    </Space.Compact>
  );

  const headerName = reportOnly ? 'Content-Security-Policy-Report-Only' : 'Content-Security-Policy';
  const preview = serializeCsp(rows);

  return (
    <div>
      <Space wrap style={{ marginBottom: 8 }}>
        <Text type="secondary">{t('Template')}</Text>
        {presetButtons}
        {onReportOnlyChange && (
          <Tooltip title={t('Browsers only report violations, nothing is blocked')}>
            <Space size={4}>
              <Switch size="small" checked={!!reportOnly} onChange={onReportOnlyChange} disabled={disabled} />
              <Text>{t('Report only')}</Text>
            </Space>
          </Tooltip>
        )}
        <Space size={4}>
          <Switch size="small" checked={raw} onChange={setRaw} />
          <Text type="secondary">{t('Edit as text')}</Text>
        </Space>
      </Space>

      {raw ? (
        <Input.TextArea
          rows={3}
          value={value}
          disabled={disabled}
          onChange={(e) => onChange?.(e.target.value)}
          placeholder="default-src 'self'; img-src *"
        />
      ) : (
        <>
          {rows.map((r, i) => {
            const flag = FLAG_DIRECTIVES.includes(r.name);
            return (
              <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 6, flexWrap: 'wrap' }}>
                <div style={{ width: 170, flex: 'none', paddingTop: 4 }}>
                  <Text code>{r.name}</Text>
                </div>
                <div style={{ flex: 1, minWidth: 200 }}>
                  {flag ? (
                    <Text type="secondary">{t('On')}</Text>
                  ) : (
                    <Space.Compact style={{ width: '100%' }}>
                      <TagsInput
                        format="array"
                        commaSeparates={false}
                        value={r.sources}
                        disabled={disabled}
                        onChange={(list) => updateRow(i, { sources: list.map(normSource) })}
                        validate={(s) => (isValidSource(s) ? null : t('Not a valid CSP source'))}
                        placeholder="'self' https://cdn.example.com"
                        maxTagCount="responsive"
                      />
                      <Select
                        value={null}
                        placeholder={<PlusOutlined />}
                        disabled={disabled}
                        style={{ width: 56 }}
                        popupMatchSelectWidth={false}
                        options={QUICK.filter((q) => !r.sources.includes(q)).map((q) => ({ value: q, label: q }))}
                        onChange={(q) => updateRow(i, { sources: q === "'none'" ? [q] : [...r.sources.filter((s) => s !== "'none'"), q] })}
                      />
                    </Space.Compact>
                  )}
                </div>
                <Button type="text" danger icon={<DeleteOutlined />} disabled={disabled} onClick={() => emit(rows.filter((_, j) => j !== i))} />
              </div>
            );
          })}
          <Select
            value={null}
            showSearch
            disabled={disabled}
            placeholder={<span><PlusOutlined /> {t('Add directive')}</span>}
            style={{ width: 220 }}
            options={addable.map((d) => ({ value: d, label: d }))}
            onChange={(d) => emit([...rows, { name: d, sources: FLAG_DIRECTIVES.includes(d) || d.startsWith('report') || d === 'sandbox' ? [] : ["'self'"] }])}
          />
        </>
      )}
      {preview && !raw && (
        <div style={{ marginTop: 8, fontFamily: 'monospace', fontSize: 12, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={`${headerName}: ${preview}`}>
          <Text type="secondary">{headerName}: {preview}</Text>
        </div>
      )}
    </div>
  );
};

export default CspBuilder;
